import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { HardHat, ArrowLeft, Mail, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ForgotPassword — email-only entry point for the reset flow. The response
 * from POST /auth/forgot-password is deliberately identical whether or not
 * the email is on file (see backend `forgot_password` for the reasoning), so
 * we always show the same green success card. This prevents attackers from
 * using the endpoint to enumerate registered users.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/auth/forgot-password`,
        { email: email.trim() },
        { withCredentials: true }
      );
      setSent(true);
    } catch (err) {
      // Backend returns generic 200 in normal operation; any 4xx/5xx here is
      // a real fault (network, throttle, bad payload). Show a soft toast but
      // still surface the "check your email" card so we don't leak intel.
      toast.error("We couldn't submit the request. Please try again in a moment.");
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1F1F1F] bi-grain px-4">
      <div className="w-full max-w-md relative z-10" data-testid="forgot-page">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-lg bg-[#FF9500] flex items-center justify-center text-[#111827]">
            <HardHat className="w-6 h-6" />
          </div>
          <div>
            <div className="font-heading font-extrabold text-white text-2xl tracking-tight leading-none">
              BUILDINTEL <span className="text-[#FF9500]">AI</span>
            </div>
            <div className="text-xs text-white/50 mt-1">Reset your password</div>
          </div>
        </div>

        <div className="bi-surface p-6 sm:p-8">
          {!sent ? (
            <>
              <h1 className="font-heading font-bold text-2xl">Forgot password?</h1>
              <p className="text-white/60 text-sm mt-1">
                Pop your email in and we&apos;ll send a reset link if the
                account exists.
              </p>
              <form onSubmit={onSubmit} className="mt-6 space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-white/60">Email</label>
                  <input
                    data-testid="forgot-email"
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@buildbiz.com.au"
                    className="mt-1 w-full bi-input"
                  />
                </div>
                <button
                  data-testid="forgot-submit"
                  type="submit"
                  disabled={busy}
                  className="w-full h-14 rounded-xl bi-accent-btn text-base flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {busy ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Sending…</>
                  ) : (
                    <><Mail className="w-5 h-5" /> Send reset link</>
                  )}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-sent" className="space-y-3">
              <div className="w-12 h-12 rounded-xl bg-[#34D399]/15 text-[#34D399] flex items-center justify-center">
                <Mail className="w-6 h-6" />
              </div>
              <h1 className="font-heading font-bold text-2xl">Check your email</h1>
              <p className="text-white/70 text-sm leading-relaxed">
                If <b className="text-white">{email}</b> is registered with
                BuildIntel AI, we&apos;ve sent a reset link. It expires in one
                hour. Check your spam folder if it doesn&apos;t arrive within a
                couple of minutes.
              </p>
              <p className="text-[11px] text-white/40 mt-2">
                Not seeing it? Double-check the address and try again.
              </p>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              data-testid="forgot-back-to-login"
              className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-[#FF9500]"
            >
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
